/**
 * Class Film — one-tap transformation of class moments into a short
 * shareable video. Not a tool, not a workflow. User clicks, picks a mood,
 * and ~20s later something beautiful appears.
 *
 * Three states rolled into one modal:
 *   1. Setup: mood picker + pick preview + "Create film →"
 *   2. Loading: soft overlay with shimmer, "Creating something special…"
 *   3. Output: autoplay video + Share / Download
 *
 * Motion/pacing is the product — we deliberately own zero timeline,
 * trimming, reordering, or caption controls. Defaults only.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { X, DownloadSimple, ShareFat } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { getMediaUrl } from '../../lib/media';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOODS = [
  { id: 'emotional', label: 'Emotional',
    hint: 'Soft piano · cinematic build' },
  { id: 'upbeat',    label: 'Upbeat',
    hint: 'Light, positive · forward motion' },
  { id: 'calm',      label: 'Calm',
    hint: 'Ambient · gentle, reflective' },
];

export default function CreateClassFilmModal({
  open, onClose, workspaceId, picks, workspaceName,
}) {
  // phase: setup | rendering | ready | error
  const [phase, setPhase] = useState('setup');
  const [mood, setMood] = useState('emotional');
  const [film, setFilm] = useState(null);
  const [err, setErr] = useState(null);
  // pickMode: 'auto' (best moments — default) | 'manual' (teacher picks)
  const [pickMode, setPickMode] = useState('auto');
  // manual mode state — fetched once when teacher switches to it
  const [allUploads, setAllUploads] = useState(null);
  const [uploadsLoading, setUploadsLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const MANUAL_MIN = 4;
  const MANUAL_MAX = 50; // matches backend CLASS_FILM_MAX_PICKS — generous
  const pollRef = useRef(null);

  useEffect(() => {
    if (!open) {
      // Reset when closed so next open is a clean setup state
      clearInterval(pollRef.current);
      setPhase('setup'); setMood('emotional'); setFilm(null); setErr(null);
      setPickMode('auto'); setSelectedIds([]); setAllUploads(null);
    }
    return () => clearInterval(pollRef.current);
  }, [open]);

  // Lazy-load all uploads only when teacher opts into manual mode — keeps
  // the modal's first-paint fast for the dominant auto-pick path.
  useEffect(() => {
    if (pickMode !== 'manual' || allUploads !== null || !workspaceId) return;
    setUploadsLoading(true);
    axios.get(`${API}/workspaces/${workspaceId}/uploads`)
      .then(({ data }) => setAllUploads((data?.items || []).filter(
        (u) => (u.content_type || '').startsWith('image/'),
      )))
      .catch(() => setAllUploads([]))
      .finally(() => setUploadsLoading(false));
  }, [pickMode, workspaceId, allUploads]);

  const eligiblePicks = useMemo(
    () => (Array.isArray(picks) ? picks.slice(0, 8) : []),
    [picks],
  );

  // Validation: manual mode requires MANUAL_MIN-MANUAL_MAX selections.
  // The bounds match the backend's CLASS_FILM_MIN_PICKS / MAX_PICKS.
  const manualReady = pickMode === 'manual'
    && selectedIds.length >= MANUAL_MIN
    && selectedIds.length <= MANUAL_MAX;
  const autoReady = pickMode === 'auto' && eligiblePicks.length >= 4;
  const canCreate = autoReady || manualReady;

  const create = async () => {
    if (!canCreate) return;
    setErr(null); setPhase('rendering');
    try {
      const body = { music: mood };
      if (pickMode === 'manual') body.picks = selectedIds;
      const { data } = await axios.post(
        `${API}/workspaces/${workspaceId}/film`, body,
      );
      // Poll every 1.8s
      pollRef.current = setInterval(async () => {
        try {
          const { data: f } = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}${data.poll_url}`,
          );
          if (f.status === 'ready') {
            clearInterval(pollRef.current);
            setFilm(f); setPhase('ready');
          } else if (f.status === 'failed') {
            clearInterval(pollRef.current);
            setErr(f.failure_reason || 'Something went wrong creating your film.');
            setPhase('error');
          }
        } catch (e) { /* swallow — next tick will try again */ }
      }, 1800);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not start film creation.');
      setPhase('error');
    }
  };

  // Exact share copy per the product brief — parent-style, not system-
  // generated. Warm, personal, zero cringe. This is the growth loop copy:
  // changing it breaks the "I got sent this" feel that makes parents forward.
  // The link points at /api/film/c/{film_id} (server-rendered HTML with OG
  // meta tags) so WhatsApp / iMessage show a rich preview card with title +
  // thumbnail. Pasting the raw R2 video URL would render as a plain link.
  const whatsappMessage = useMemo(() => {
    const backend = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
    const shareLink = film?.film_id
      ? `${backend}/api/film/c/${film.film_id}`
      : (film?.video_url || '');
    const line = [
      'Hi! We got sent this from school today ❤️',
      '',
      'It’s a short film of the class — thought you’d love it:',
      '',
      shareLink,
    ].join('\n');
    return encodeURIComponent(line);
  }, [film?.film_id, film?.video_url]);

  if (!open) return null;

  return (
    <div
      data-testid="class-film-modal"
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(15,12,8,0.55)', backdropFilter: 'blur(6px)' }}
      onClick={(e) => { if (e.currentTarget === e.target) onClose(); }}
    >
      <div
        className="relative w-full max-w-lg mx-4"
        style={{
          background: '#FFFFFF', borderRadius: '16px', padding: '24px 22px 20px',
          boxShadow: '0 30px 80px rgba(20,14,6,0.35)',
          fontFamily: "Georgia, 'Times New Roman', serif",
        }}
      >
        <button
          onClick={onClose}
          data-testid="class-film-close"
          aria-label="Close"
          style={{
            position: 'absolute', top: 14, right: 14,
            background: 'none', border: 'none', cursor: 'pointer', color: '#7A6F5C',
          }}
        ><X size={20} /></button>

        {phase === 'setup' && (
          <SetupView
            eligiblePicks={eligiblePicks}
            mood={mood} setMood={setMood}
            onCreate={create}
            pickMode={pickMode} setPickMode={setPickMode}
            allUploads={allUploads}
            uploadsLoading={uploadsLoading}
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
            canCreate={canCreate}
            manualReady={manualReady}
            manualMin={MANUAL_MIN}
            manualMax={MANUAL_MAX}
          />
        )}
        {phase === 'rendering' && <LoadingView />}
        {phase === 'ready' && film && (
          <OutputView
            film={film}
            whatsappMessage={whatsappMessage}
            workspaceName={workspaceName}
            workspaceId={workspaceId}
          />
        )}
        {phase === 'error' && (
          <ErrorView message={err} onRetry={() => setPhase('setup')} />
        )}
      </div>
    </div>
  );
}

const SetupView = ({
  eligiblePicks, mood, setMood, onCreate,
  pickMode, setPickMode, allUploads, uploadsLoading,
  selectedIds, setSelectedIds, canCreate, manualReady,
  manualMin, manualMax,
}) => {
  const togglePick = (uploadId) => {
    setSelectedIds((prev) => {
      if (prev.includes(uploadId)) return prev.filter((id) => id !== uploadId);
      if (prev.length >= manualMax) return prev; // hard cap at backend max
      return [...prev, uploadId];
    });
  };

  // Reorder helpers — `reorderPick` swaps two selected tiles by their
  // position in `selectedIds`. The on-tile ◀/▶ buttons call this with
  // ±1 deltas; the HTML5 drag handlers compute the delta from the
  // drop target. Either input path produces the same state mutation
  // so the order strip stays consistent regardless of input device.
  const reorderPick = (uploadId, targetIdx) => {
    setSelectedIds((prev) => {
      const fromIdx = prev.indexOf(uploadId);
      if (fromIdx === -1) return prev;
      const clamped = Math.max(0, Math.min(prev.length - 1, targetIdx));
      if (clamped === fromIdx) return prev;
      const next = prev.slice();
      next.splice(fromIdx, 1);
      next.splice(clamped, 0, uploadId);
      return next;
    });
  };
  // Native HTML5 drag — desktop power-user path. Mobile users get
  // the ◀/▶ buttons (touch drag is unreliable across iOS Safari +
  // Android Chrome without a heavyweight DnD library).
  const onDragStart = (e, uploadId) => {
    e.dataTransfer.setData('text/plain', uploadId);
    e.dataTransfer.effectAllowed = 'move';
  };
  const onDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  const onDrop = (e, targetUploadId) => {
    e.preventDefault();
    const sourceId = e.dataTransfer.getData('text/plain');
    if (!sourceId || sourceId === targetUploadId) return;
    const targetIdx = selectedIds.indexOf(targetUploadId);
    if (targetIdx !== -1) reorderPick(sourceId, targetIdx);
  };

  return (
    <>
      <div style={{
        fontFamily: 'Helvetica, Arial, sans-serif', fontSize: 11,
        letterSpacing: '0.18em', textTransform: 'uppercase', color: '#9A8B6B',
        paddingBottom: 8,
      }}>
        🎬 Class Film
      </div>
      <h2 style={{
        margin: 0, fontSize: 24, fontWeight: 400, color: '#1F1B14',
        lineHeight: 1.25, paddingBottom: 6,
      }}>
        Create your class film
      </h2>
      <p style={{
        margin: 0, fontSize: 14, color: '#5B5349', lineHeight: 1.55,
        paddingBottom: 16,
      }}>
        {pickMode === 'auto'
          ? 'We’ve picked your best moments. Pick a feel — we’ll do the rest.'
          : '✨ For the best film, 6–12 photos works beautifully. You can choose more if you’d like.'}
      </p>

      {/* Mode toggle — Best moments | Pick your own.
         Segmented pill so the choice feels equal in weight, not a
         "settings" buried option. */}
      <div
        data-testid="class-film-pick-mode"
        style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4,
          padding: 4, marginBottom: 16,
          background: '#F1ECE0',
          borderRadius: 999,
        }}
      >
        {[
          { id: 'auto', label: '✨ Best moments', testId: 'pick-mode-auto' },
          { id: 'manual', label: '👆 Pick your own', testId: 'pick-mode-manual' },
        ].map((opt) => {
          const active = pickMode === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => setPickMode(opt.id)}
              data-testid={opt.testId}
              style={{
                padding: '10px 14px', border: 'none', borderRadius: 999,
                background: active ? '#FFFFFF' : 'transparent',
                color: active ? '#1F1B14' : '#7A6F5C',
                fontFamily: 'Helvetica, Arial, sans-serif',
                fontSize: 13, fontWeight: active ? 600 : 500,
                letterSpacing: '0.02em', cursor: 'pointer',
                boxShadow: active ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
                transition: 'background 160ms ease, color 160ms ease',
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>

      {/* AUTO MODE — show the algorithm's picks, read-only */}
      {pickMode === 'auto' && (
        <div
          data-testid="class-film-picks"
          style={{
            display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 6, paddingBottom: 18,
          }}
        >
          {eligiblePicks.map((p) => (
            <div key={p.upload_id} style={{
              position: 'relative', aspectRatio: '1 / 1',
              borderRadius: 8, overflow: 'hidden', background: '#F7F4EE',
            }}>
              <img
                src={getMediaUrl(p, 'thumb_400')}
                alt=""
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </div>
          ))}
        </div>
      )}

      {/* MANUAL MODE — tappable grid of all workspace uploads */}
      {pickMode === 'manual' && (
        <>
          <div
            data-testid="class-film-manual-counter"
            style={{
              fontFamily: 'Helvetica, Arial, sans-serif', fontSize: 12,
              color: manualReady ? '#3F7E4E' : '#7A6F5C',
              paddingBottom: 8, letterSpacing: '0.02em',
            }}
          >
            {selectedIds.length === 0 && `Tap photos to pick · at least ${manualMin}`}
            {selectedIds.length > 0 && selectedIds.length < manualMin &&
              `${selectedIds.length} selected · ${manualMin - selectedIds.length} more · ◀ ▶ to reorder`}
            {selectedIds.length >= manualMin && selectedIds.length < manualMax &&
              `${selectedIds.length} selected ✓ · ◀ ▶ to reorder`}
            {selectedIds.length >= manualMax &&
              `${selectedIds.length} selected · max reached · ◀ ▶ to reorder`}
          </div>
          {uploadsLoading && (
            <div data-testid="class-film-manual-loading" style={{
              padding: '24px 0', textAlign: 'center',
              color: '#7A6F5C', fontSize: 13,
            }}>Loading your photos…</div>
          )}
          {!uploadsLoading && allUploads && allUploads.length === 0 && (
            <div style={{
              padding: '24px 0', textAlign: 'center',
              color: '#7A6F5C', fontSize: 13,
            }}>No photos yet — upload some first.</div>
          )}
          {!uploadsLoading && allUploads && allUploads.length > 0 && (
            <div
              data-testid="class-film-manual-grid"
              style={{
                display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 6, paddingBottom: 18,
                maxHeight: 360, overflowY: 'auto',
                marginRight: -4, paddingRight: 4, // soft scrollbar inset
              }}
            >
              {allUploads.map((u) => {
                const idx = selectedIds.indexOf(u.upload_id);
                const isSelected = idx !== -1;
                const atCap = !isSelected && selectedIds.length >= manualMax;
                return (
                  <div
                    key={u.upload_id}
                    data-testid={`pick-tile-wrap-${u.upload_id}`}
                    draggable={isSelected}
                    onDragStart={isSelected ? (e) => onDragStart(e, u.upload_id) : undefined}
                    onDragOver={isSelected ? onDragOver : undefined}
                    onDrop={isSelected ? (e) => onDrop(e, u.upload_id) : undefined}
                    style={{ position: 'relative' }}
                  >
                    <button
                      type="button"
                      onClick={() => togglePick(u.upload_id)}
                      disabled={atCap}
                      data-testid={`pick-tile-${u.upload_id}`}
                      style={{
                        position: 'relative', width: '100%', aspectRatio: '1 / 1',
                        borderRadius: 8, overflow: 'hidden',
                        background: '#F7F4EE',
                        padding: 0, border: isSelected
                          ? '2px solid #1F1B14'
                          : '2px solid transparent',
                        cursor: atCap ? 'not-allowed' : (isSelected ? 'grab' : 'pointer'),
                        opacity: atCap ? 0.4 : 1,
                        transition: 'border-color 140ms ease, transform 140ms ease',
                        transform: isSelected ? 'scale(0.96)' : 'scale(1)',
                      }}
                    >
                      <img
                        src={getMediaUrl(u, 'thumb_400')}
                        alt=""
                        style={{
                          width: '100%', height: '100%', objectFit: 'cover',
                          display: 'block',
                        }}
                      />
                      {isSelected && (
                        <span style={{
                          position: 'absolute', top: 6, right: 6,
                          width: 22, height: 22, borderRadius: 999,
                          background: '#1F1B14', color: '#FFFFFF',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 11, fontWeight: 700,
                          boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
                        }}>{idx + 1}</span>
                      )}
                    </button>
                    {/* Reorder controls — appear on selected tiles only.
                        Touch-friendly arrow buttons are the primary
                        reorder UX (HTML5 drag works on desktop too,
                        wired via the wrapper div above). The ◀/▶
                        buttons stop click bubbling so tapping them
                        doesn't accidentally toggle the photo off. */}
                    {isSelected && (
                      <div style={{
                        position: 'absolute', bottom: 4, left: 4, right: 4,
                        display: 'flex', gap: 4, justifyContent: 'space-between',
                        pointerEvents: 'none',
                      }}>
                        <button
                          type="button"
                          aria-label="Move earlier"
                          data-testid={`pick-tile-up-${u.upload_id}`}
                          disabled={idx === 0}
                          onClick={(e) => { e.stopPropagation(); reorderPick(u.upload_id, idx - 1); }}
                          style={{
                            pointerEvents: 'auto',
                            width: 26, height: 26, borderRadius: 6,
                            background: idx === 0 ? 'rgba(0,0,0,0.25)' : 'rgba(0,0,0,0.7)',
                            color: '#FFFFFF', border: 'none',
                            fontSize: 14, fontWeight: 700, cursor: idx === 0 ? 'not-allowed' : 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            opacity: idx === 0 ? 0.4 : 1,
                          }}
                        >◀</button>
                        <button
                          type="button"
                          aria-label="Move later"
                          data-testid={`pick-tile-down-${u.upload_id}`}
                          disabled={idx === selectedIds.length - 1}
                          onClick={(e) => { e.stopPropagation(); reorderPick(u.upload_id, idx + 1); }}
                          style={{
                            pointerEvents: 'auto',
                            width: 26, height: 26, borderRadius: 6,
                            background: idx === selectedIds.length - 1 ? 'rgba(0,0,0,0.25)' : 'rgba(0,0,0,0.7)',
                            color: '#FFFFFF', border: 'none',
                            fontSize: 14, fontWeight: 700, cursor: idx === selectedIds.length - 1 ? 'not-allowed' : 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            opacity: idx === selectedIds.length - 1 ? 0.4 : 1,
                          }}
                        >▶</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      <div style={{
        fontFamily: 'Helvetica, Arial, sans-serif', fontSize: 11,
        letterSpacing: '0.12em', textTransform: 'uppercase', color: '#6B5F4A',
        paddingBottom: 10,
      }}>
        🎵 Choose a feel
      </div>
      <div style={{ display: 'grid', gap: 8, paddingBottom: 18 }}>
        {MOODS.map((m) => (
          <label
            key={m.id}
            data-testid={`class-film-mood-${m.id}`}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 12px',
              border: mood === m.id ? '2px solid #1F1B14' : '1px solid #EFE9DF',
              borderRadius: 10, cursor: 'pointer', background: '#FFFFFF',
            }}
          >
            <input
              type="radio" name="mood" value={m.id} checked={mood === m.id}
              onChange={() => setMood(m.id)}
              style={{ accentColor: '#1F1B14' }}
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, color: '#1F1B14' }}>{m.label}</div>
              <div style={{
                fontSize: 12, color: '#8A7F6B',
                fontFamily: 'Helvetica, Arial, sans-serif',
              }}>{m.hint}</div>
            </div>
          </label>
        ))}
      </div>

      <button
        data-testid="class-film-create"
        onClick={onCreate}
        disabled={!canCreate}
        className="w-full"
        style={{
          padding: '14px 22px',
          background: canCreate ? '#111111' : '#C8C2B5',
          color: '#FFFFFF',
          border: 'none', borderRadius: 999,
          fontFamily: 'Helvetica, Arial, sans-serif', fontSize: 14,
          fontWeight: 600, letterSpacing: '0.04em',
          cursor: canCreate ? 'pointer' : 'not-allowed',
        }}
      >
        Create film →
      </button>
    </>
  );
};

const LoadingView = () => (
  <div style={{ padding: '44px 20px 28px', textAlign: 'center' }}>
    <div
      aria-hidden
      style={{
        width: 80, height: 80, margin: '0 auto 22px',
        borderRadius: 999,
        background: 'linear-gradient(135deg,#E7E0CF 0%,#F7F4EE 50%,#E7E0CF 100%)',
        backgroundSize: '200% 200%',
        animation: 'class-film-shimmer 2.2s ease-in-out infinite',
      }}
    />
    <div style={{ fontSize: 22, fontWeight: 400, color: '#1F1B14' }}>
      ✨ We’re creating your class film…
    </div>
    <div style={{
      marginTop: 8, fontSize: 13, color: '#7A6F5C',
      fontFamily: 'Helvetica, Arial, sans-serif',
    }}>
      This takes about 10–15 seconds
    </div>
    <style>{`
      @keyframes class-film-shimmer {
        0%,100% { background-position: 0% 0%; }
        50%     { background-position: 100% 100%; }
      }
    `}</style>
  </div>
);

const OutputView = ({ film, whatsappMessage, workspaceName, workspaceId }) => {
  const shareUrl = `https://wa.me/?text=${whatsappMessage}`;
  // 0.8s reveal delay — lets the film land emotionally before the CTAs
  // interrupt. Per the brief: "delay buttons by 0.8 seconds · lets emotion
  // land · avoids UI interrupting moment".
  const [showCtas, setShowCtas] = useState(false);
  useEffect(() => {
    setShowCtas(false);
    const t = setTimeout(() => setShowCtas(true), 800);
    return () => clearTimeout(t);
  }, [film?.film_id]);
  return (
    <>
      <div style={{
        fontFamily: 'Helvetica, Arial, sans-serif', fontSize: 11,
        letterSpacing: '0.18em', textTransform: 'uppercase', color: '#9A8B6B',
        paddingBottom: 10,
      }}>
        ✨ Your class film is ready
      </div>
      <video
        data-testid="class-film-video"
        src={film.video_url}
        autoPlay loop muted playsInline
        controls
        style={{
          width: '100%', aspectRatio: '1 / 1', borderRadius: 12,
          background: '#000', display: 'block',
        }}
      />
      <div
        data-testid="class-film-ctas"
        style={{
          opacity: showCtas ? 1 : 0,
          transform: showCtas ? 'translateY(0)' : 'translateY(6px)',
          transition: 'opacity 500ms ease-out, transform 500ms ease-out',
          pointerEvents: showCtas ? 'auto' : 'none',
        }}
      >
        <div style={{
          display: 'grid', gap: 10, paddingTop: 16,
          gridTemplateColumns: '1fr 1fr',
        }}>
          <a
            href={`/assembly/${workspaceId}/${film.film_id}`}
            data-testid="class-film-play-assembly"
            style={{
              padding: '12px 16px', background: '#111111', color: '#FFFFFF',
              textDecoration: 'none', borderRadius: 999, textAlign: 'center',
              fontFamily: 'Helvetica, Arial, sans-serif', fontSize: 13,
              fontWeight: 600, letterSpacing: '0.04em',
              display: 'inline-flex', gap: 8, justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            ▶ Play in Assembly Mode
          </a>
          <a
            href={shareUrl}
            target="_blank" rel="noreferrer"
            data-testid="class-film-share"
            style={{
              padding: '12px 16px', background: '#FFFFFF', color: '#1F1B14',
              border: '1px solid #E7E0CF',
              textDecoration: 'none', borderRadius: 999, textAlign: 'center',
              fontFamily: 'Helvetica, Arial, sans-serif', fontSize: 13,
              fontWeight: 600, letterSpacing: '0.04em',
              display: 'inline-flex', gap: 8, justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <ShareFat size={16} /> Share with parents
          </a>
        </div>
        <div style={{
          display: 'flex', justifyContent: 'center', paddingTop: 10,
        }}>
          <a
            href={film.video_url}
            download={`${(workspaceName || 'class').replace(/\W+/g,'-').toLowerCase()}-film.mp4`}
            data-testid="class-film-download"
            style={{
              fontSize: 12, color: '#7A6F5C',
              fontFamily: 'Helvetica, Arial, sans-serif',
              textDecoration: 'underline', textUnderlineOffset: 2,
              display: 'inline-flex', gap: 6, alignItems: 'center',
            }}
          >
            <DownloadSimple size={12} /> Download
          </a>
        </div>
        <p style={{
          marginTop: 14, textAlign: 'center', fontSize: 12, color: '#7A6F5C',
          fontFamily: 'Helvetica, Arial, sans-serif',
        }}>
          Made from your class moments ❤️
        </p>
      </div>
    </>
  );
};

const ErrorView = ({ message, onRetry }) => (
  <div style={{ padding: '32px 14px', textAlign: 'center' }}>
    <div style={{ fontSize: 18, fontWeight: 400, color: '#1F1B14' }}>
      We couldn’t finish this one.
    </div>
    <div style={{
      marginTop: 8, fontSize: 13, color: '#7A6F5C',
      fontFamily: 'Helvetica, Arial, sans-serif',
    }}>
      {message}
    </div>
    <button
      onClick={onRetry}
      data-testid="class-film-retry"
      style={{
        marginTop: 20, padding: '12px 22px',
        background: '#111111', color: '#FFFFFF',
        border: 'none', borderRadius: 999,
        fontFamily: 'Helvetica, Arial, sans-serif', fontSize: 13,
        fontWeight: 600, cursor: 'pointer',
      }}
    >Try again</button>
  </div>
);
